import { useQuery } from "@tanstack/react-query";
import "./status.css";
import { AppHeader } from "../components/AppHeader";
import { LogoBand } from "../components/LogoBand";
import { useSettings } from "../components/useSettings";
import { StatusUnavailable, fetchIncident, fetchIncidents } from "./api";
import { hrefFor } from "./route";
import { IncidentCard } from "./IncidentCard";
import type { Incident } from "./types";

/** All published incidents, newest first. */
export function IncidentsPage() {
  useSettings();

  const { data, isPending, isError } = useQuery({
    queryKey: ["status", "incidents"],
    queryFn: fetchIncidents,
    staleTime: 60_000,
    retry: 1,
  });

  return (
    <>
      <AppHeader />
      <LogoBand />
      <main className="status-main" id="main">
        <a className="status-back" href={hrefFor("/status")}>
          ← Back to service status
        </a>
        <div className="status-head">
          <h1>Incident history</h1>
          <p>Past and current issues affecting LLR Work and Health Support.</p>
        </div>

        <div className="status-card">
          {isPending && <div className="status-empty">Loading…</div>}
          {isError && (
            <div className="status-empty">
              We cannot show incident history right now.
            </div>
          )}
          {data && data.incidents.length === 0 && (
            <div className="status-empty">No incidents have been reported.</div>
          )}
          {data?.incidents.map((inc) => (
            <IncidentCard key={inc.reference} incident={inc} linkToDetail collapsed />
          ))}
        </div>
      </main>
    </>
  );
}

/** One incident and its full timeline. */
export function IncidentPage({ reference }: { reference: string }) {
  useSettings();

  const { data, isPending, error } = useQuery<Incident>({
    queryKey: ["status", "incident", reference],
    queryFn: () => fetchIncident(reference),
    staleTime: 30_000,
    retry: (count, err) =>
      // A 404 is a real answer (unknown or unpublished incident), not a blip.
      !(err instanceof StatusUnavailable && err.status === 404) && count < 1,
  });

  const notFound = error instanceof StatusUnavailable && error.status === 404;

  return (
    <>
      <AppHeader />
      <LogoBand />
      <main className="status-main" id="main">
        <a className="status-back" href={hrefFor("/status")}>
          ← Back to service status
        </a>

        {isPending && <div className="status-empty">Loading…</div>}

        {notFound && (
          <>
            <div className="status-head">
              <h1>Incident not found</h1>
              <p>
                We could not find an incident with the reference{" "}
                <strong>{reference}</strong>. It may have been removed, or the
                link may be incomplete.
              </p>
            </div>
            <p className="status-footer">
              <a href={hrefFor("/status/incidents")}>See all incidents</a>
            </p>
          </>
        )}

        {error && !notFound && (
          <div className="status-empty">We cannot show this incident right now.</div>
        )}

        {data && (
          <>
            <div className="status-head">
              <h1>{data.title}</h1>
            </div>
            <div className="status-card">
              <IncidentCard incident={data} showTitle={false} />
            </div>
          </>
        )}
      </main>
    </>
  );
}
